import { useEffect, useRef, useState } from 'react';

import { NavLink, useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import InboxTwoToneIcon from '@mui/icons-material/InboxTwoTone';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { BaseAPI } from 'src/providers/base-api';
import { UsersInterface } from 'src/models/CommonInterface';
import { useAuth } from 'src/hooks/useAuth';
import { useCodeTables } from 'src/hooks/useCodeTables';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
        `
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
  background: ${theme.colors.alpha.black[5]};
  padding: ${theme.spacing(2)};
  `
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
  text-align: left;
  padding-left: ${theme.spacing(1)};
  `
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
  font-weight: ${theme.typography.fontWeightBold};
  color: ${theme.palette.secondary.main};
  display: block;
  text-transform: capitalize
  `
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
  color: ${lighten(theme.palette.secondary.main, 0.5)}
  `
);

function HeaderUserbox() {
  // Use the useAuth hook for user data from Redux
  const { user, isAuthenticated, fullName, firstName, lastName, logout } = useAuth();
  const { getNameById } = useCodeTables();

  const navigate = useNavigate();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState<string>('/static/images/avatars/user.png');
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState<boolean>(false);

  // Derive profile picture URL directly from the public streaming endpoint.
  // No async fetch needed — URL is stable (nanoID-based) and browser-cacheable.
  useEffect(() => {
    const profilePictureId = user?.employeeId?.profilePicture;
    if (profilePictureId) {
      const url = BaseAPI.buildProfilePictureURL(profilePictureId);
      if (url) {
        setImgSrc(url);
      }
    }
  }, [user?.employeeId?.profilePicture]);

  // Check for session issues - if authenticated in Redux but user data is missing
  useEffect(() => {
    const checkSession = async () => {
      // If we're supposed to be authenticated but have no user data, there might be an issue
      if (!user && sessionStorage.getItem('LoginInfo')) {
        try {
          const response = await BaseAPI.validateToken();
          if (!response || !response.data) {
            setSessionExpiredOpen(true);
          }
        } catch (error) {
          setSessionExpiredOpen(true);
        }
      }
    };

    // Small delay to allow Redux hydration to complete
    const timeoutId = setTimeout(checkSession, 1000);
    return () => clearTimeout(timeoutId);
  }, [user]);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await BaseAPI.logout('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Always clear session and redirect, even if API fails
      sessionStorage.clear();
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict';
      window.location.href = '/login';
    }
  };

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    handleLogout();
  };

  const handleSignInNewTab = () => {
    window.open('/login', '_blank');
    setSessionExpiredOpen(false);
  };

  // Display name - use data from useAuth hook
  const displayFirstName = firstName || '';
  const displayLastName = lastName || '';
  const displayFullName = fullName || 'User';

  // Get party type name from code tables using user.partyType ID
  const displayPartyType = user?.partyType ? getNameById('user_ref', user.partyType) : '';

  // Always render something - even if user data is unavailable
  const hasUserData = displayFirstName || displayLastName;

  return (
    <>
      {/* Session Expired Dialog */}
      <Dialog open={sessionExpiredOpen} onClose={() => { }} disableEscapeKeyDown aria-labelledby="session-expired-dialog-title">
        <DialogTitle id="session-expired-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="warning" />
          Session Expired
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Your session has expired or is invalid. Please sign in again to continue.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSignInNewTab} color="primary">
            Sign in (New Tab)
          </Button>
          <Button onClick={handleSessionExpiredClose} color="primary" variant="contained">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Box - Always render */}
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar variant="rounded" alt={displayFirstName || 'User'} src={imgSrc} />
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{hasUserData ? `${displayFirstName} ${displayLastName}` : 'Menu'}</UserBoxLabel>
            {displayPartyType && <UserBoxDescription variant="body2">{displayPartyType}</UserBoxDescription>}
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>

      <Popover
        anchorEl={ref.current}
        sx={{ top: '70px', marginRight: '10px', backdropFilter: 'blur(0px)' }}
        onClose={handleClose}
        open={isOpen}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar variant="rounded" alt={displayFirstName || 'User'} src={imgSrc} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{hasUserData ? `${displayFirstName} ${displayLastName}` : 'User'}</UserBoxLabel>
            {displayPartyType && <UserBoxDescription variant="body2">{displayPartyType}</UserBoxDescription>}
          </UserBoxText>
        </MenuUserBox>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button
            color="primary"
            fullWidth
            component={NavLink}
            to="/my-profile"
            onClick={handleClose}
            sx={{ mb: 1 }}
          >
            <AccountBoxTwoToneIcon sx={{ mr: 1 }} />
            My Profile
          </Button>
          <Button color="primary" fullWidth onClick={handleLogout}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
